import React, { useEffect, useState } from 'react';

import { PanelsEnum } from '@ariksa/inventory-core/api';
import { useDisclosure } from '@chakra-ui/react';
import { isEmpty, map, orderBy, sumBy } from 'lodash';
import { useSelector } from 'react-redux';

import { formatNumberWithComma, getIconColor } from 'components/DataDisplay';
import formatNumber from 'components/DataDisplay/Utils/formatNumber';
import { DashboardWidget } from 'components/Visualization/CDashboard/Widget/DashboardWidget';
import { selectCloudPostureOverview } from 'containers/Dashboard/CloudPostureOverview/selectors';
import { AllEntitiesDrawer } from 'containers/Dashboard/Drawers/AllEntitiesDrawer';
import { AllEntitiesDetailsProps } from 'containers/Dashboard/types';
import { renderResourcesWithIconAndCount } from 'containers/Dashboard/utils/utils';

export const TopPubliclyAccessibleMachinesAndDataSources: React.FC = () => {
  const { publiclyAccessibleSources } = useSelector(selectCloudPostureOverview);
  const allEntitiesDisclosure = useDisclosure();
  const [details, setDetails] = useState<AllEntitiesDetailsProps>(
    {} as AllEntitiesDetailsProps,
  );
  const [total, setTotal] = useState(0);

  useEffect(() => {
    setTotal(sumBy(publiclyAccessibleSources.data, o => o.resource_count ?? 0));
  }, [publiclyAccessibleSources.data]);

  return (
    <>
      <DashboardWidget
        isLoading={publiclyAccessibleSources.isLoading}
        label="Publicly Accessible Entities"
        subHeader={
          publiclyAccessibleSources.data?.length +
          ' entity type' +
          (publiclyAccessibleSources.data?.length === 1 ? '' : 's') +
          ', ' +
          formatNumberWithComma(total) +
          ' entit' +
          (total === 1 ? 'y' : 'ies')
        }
        noData={isEmpty(publiclyAccessibleSources.data)}
        noDataMessage="No insights available"
        content={renderResourcesWithIconAndCount(
          map(
            orderBy(
              publiclyAccessibleSources.data,
              // Treat missing counts as 0 so they sort last instead of first in desc order.
              [o => o.resource_count ?? 0],
              ['desc'],
            ),
            o => ({
              label: o.agnostic_name!,
              iconType: o.agnostic_class!,
              count: formatNumber(o?.resource_count),
              iconColor: getIconColor(o.agnostic_class!),
              borderColor: getIconColor(o.agnostic_class!),
              onClick: () => {
                allEntitiesDisclosure.onOpen();
                setDetails({
                  panel: PanelsEnum.PubliclyAccessibleEntities,
                  iconType: o.agnostic_class!,
                  resourceType: o.agnostic_class!,
                  resourceTypeClass: 'agnostic',
                  total: o?.resource_count ?? 0,
                });
              },
            }),
          ),
        )}
        styles={{ contentWrapper: { px: 0, pb: 0 } }}
      />
      <AllEntitiesDrawer
        isOpen={allEntitiesDisclosure.isOpen}
        onClose={allEntitiesDisclosure.onClose}
        {...details}
      />
    </>
  );
};
